import { Box, Button, Collapse, Icon, Paper, Typography } from '@shared/ui';
import React from 'react';

interface ResultFormatterProps {
  data: any;
}

/**
 * Smart result formatter that detects data type and renders appropriately
 */
export function ResultFormatter({ data }: ResultFormatterProps) {
  const [showRaw, setShowRaw] = React.useState(false);

  // Detect result type and render accordingly
  const formattedContent = React.useMemo(() => {
    if (!data) {
      return (
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
      );
    }

    // Check for various common result patterns
    if (isDocumentList(data)) {
      return <DocumentList data={data} />;
    }

    if (isTextContent(data)) {
      return <TextContent text={data.text || data.content || data.answer} />;
    }

    if (isPlanResult(data)) {
      return <PlanSummary plan={data.plan || data.strategy} />;
    }

    if (isToolResult(data)) {
      return <ToolResultView result={data} />;
    }

    // Default: try to extract readable content
    const readableText = extractReadableText(data);
    if (readableText) {
      return <TextContent text={readableText} />;
    }

    // Fallback: show expandable JSON
    setShowRaw(true);
    return null;
  }, [data]);

  return (
    <Box>
      {formattedContent}

      {!showRaw && formattedContent && (
        <Button
          size="small"
          startIcon={<Icon>code</Icon>}
          onClick={() => setShowRaw(!showRaw)}
          sx={{ mt: 1 }}
        >
          {showRaw ? 'Hide' : 'Show'} Raw Data
        </Button>
      )}

      <Collapse in={showRaw}>
        <Paper variant="outlined" sx={{ mt: 2, p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Raw JSON Data:
          </Typography>
          <pre
            style={{
              fontSize: '11px',
              overflow: 'auto',
              maxHeight: '300px',
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {JSON.stringify(data, null, 2)}
          </pre>
        </Paper>
      </Collapse>
    </Box>
  );
}

// Type detection functions
function isDocumentList(data: any): boolean {
  return !!(
    data &&
    (Array.isArray(data.documents) || Array.isArray(data.items) || Array.isArray(data.results)) &&
    (data.documents?.[0]?.id ||
      data.documents?.[0]?.doc_id ||
      data.items?.[0]?.id ||
      data.results?.[0]?.id)
  );
}

function isTextContent(data: any): boolean {
  return !!(
    data &&
    typeof data === 'object' &&
    (data.text || data.content || data.answer) &&
    typeof (data.text || data.content || data.answer) === 'string'
  );
}

function isPlanResult(data: any): boolean {
  return !!(
    data &&
    typeof data === 'object' &&
    (data.plan || data.strategy) &&
    (data.plan?.steps || data.strategy?.steps)
  );
}

function isToolResult(data: any): boolean {
  return !!(
    data &&
    typeof data === 'object' &&
    (data.ok !== undefined || data.success !== undefined || data.error !== undefined)
  );
}

function extractReadableText(data: any): string | null {
  if (typeof data === 'string') return data;
  if (typeof data === 'number' || typeof data === 'boolean') return String(data);

  // Try common text fields
  const textFields = ['message', 'description', 'summary', 'result', 'output'];
  for (const field of textFields) {
    if (data[field] && typeof data[field] === 'string') {
      return data[field];
    }
  }

  return null;
}

// Formatter Components
function DocumentList({ data }: { data: any }) {
  const documents = data.documents || data.items || data.results || [];

  if (documents.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No documents found
      </Typography>
    );
  }

  return (
    <Box>
      {/* Metadata section */}
      {(data.search_strategy || data.coverage_confidence !== undefined) && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'info.50', borderColor: 'info.200' }}>
          {data.search_strategy && (
            <Box sx={{ mb: 1 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontWeight: 'medium' }}
              >
                Search Strategy
              </Typography>
              <Typography variant="body2">{data.search_strategy}</Typography>
            </Box>
          )}
          {data.coverage_confidence !== undefined && (
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontWeight: 'medium' }}
              >
                Coverage Confidence
              </Typography>
              <Typography variant="body2" color="primary.main">
                {(data.coverage_confidence * 100).toFixed(0)}%
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Documents list */}
      <Typography variant="subtitle2" gutterBottom>
        Documents ({documents.length})
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {documents.slice(0, 10).map((doc: any, idx: number) => (
          <Paper key={doc.id || doc.doc_id || idx} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              {doc.title || doc.name || `Document ${idx + 1}`}
            </Typography>

            {/* Doc ID */}
            {(doc.doc_id || doc.id) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontFamily: 'monospace', mb: 0.5 }}
              >
                ID: {doc.doc_id || doc.id}
              </Typography>
            )}

            {/* Last Modified */}
            {doc.last_modified && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 0.5 }}
              >
                Last Modified: {new Date(doc.last_modified).toLocaleDateString()}
              </Typography>
            )}

            {/* Relevance Note */}
            {doc.relevance_note && (
              <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 1 }}>
                📌 {doc.relevance_note}
              </Typography>
            )}

            {/* Summary */}
            {doc.summary && (
              <Typography variant="body2" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {doc.summary}
              </Typography>
            )}

            {/* Score */}
            {doc.score !== undefined && (
              <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 1 }}>
                Relevance Score: {(doc.score * 100).toFixed(0)}%
              </Typography>
            )}
          </Paper>
        ))}
        {documents.length > 10 && (
          <Typography variant="caption" color="text.secondary">
            ... and {documents.length - 10} more documents
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function TextContent({ text }: { text: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {text}
      </Typography>
    </Paper>
  );
}

function PlanSummary({ plan }: { plan: any }) {
  if (!plan?.steps) {
    return (
      <Typography variant="body2" color="text.secondary">
        No plan data
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        Execution Plan ({plan.steps.length} steps)
      </Typography>
      {plan.goal_understanding && (
        <Typography variant="body2" color="text.secondary" paragraph>
          {plan.goal_understanding}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
        {plan.steps.map((step: any, idx: number) => (
          <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" fontWeight="medium">
              {idx + 1}. {step.description}
            </Typography>
            {step.required_tools && step.required_tools.length > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.5 }}
              >
                Tools: {step.required_tools.join(', ')}
              </Typography>
            )}
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

function ToolResultView({ result }: { result: any }) {
  const isSuccess = result.ok === true || result.success === true;
  const isError = result.ok === false || result.success === false || !!result.error;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: isError ? 'error.50' : isSuccess ? 'success.50' : 'grey.50',
        borderColor: isError ? 'error.main' : isSuccess ? 'success.main' : 'grey.300',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Icon
          sx={{ color: isError ? 'error.main' : isSuccess ? 'success.main' : 'text.secondary' }}
        >
          {isError ? 'error' : isSuccess ? 'check_circle' : 'info'}
        </Icon>
        <Typography variant="subtitle2">
          {isError ? 'Error' : isSuccess ? 'Success' : 'Result'}
        </Typography>
      </Box>

      {result.error && (
        <Typography variant="body2" color="error.main">
          {typeof result.error === 'string' ? result.error : JSON.stringify(result.error)}
        </Typography>
      )}

      {result.message && <Typography variant="body2">{result.message}</Typography>}

      {/* Show count of items if available */}
      {result.count !== undefined && (
        <Typography variant="body2" color="text.secondary">
          Count: {result.count}
        </Typography>
      )}
    </Paper>
  );
}
